import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from 'components-antd';
import { showSuccessMessage } from 'helpers';

import { markNotificationAsReadEffect } from 'store/effects/notifications';
import { Avatar } from 'components';

import moment from 'moment';
import { ArrowRight } from 'components/Icons';
import { Icons as Icon } from '../Icons';

import Title from '../../Title';
import Subtitle from '../../Subtitle';

import styles from './styles.module.scss';
import { Tag } from 'components-antd';
import BaseSummaryNotification from '../BaseSummaryNotification';
import { clearNotification, toggleNotification } from 'services/sockets';
import {
  requestMarkNotificationAsClearedAction,
  requestMarkNotificationToggleAction,
} from 'store/actions/notifications';
import { getNotificationsPaginationSelector } from 'store/selectors/sockets/notifications';

const SeverityLevel = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

const BaseNotification = (props) => {
  const {
    className,
    ids,
    avatarUrl,
    avatarPlaceholder,
    title,
    subtitle,
    date,
    isRead,
    severityLevel,
    notificationSummary,
    IsPinned = false,
  } = props;
  const { onClick, groupType, markable, onMarkAllAsRead } = props;
  const { pinned } = useSelector(getNotificationsPaginationSelector);

  const [openMenu, setOpenMenu] = useState(false);

  const [actionPosition, setActionPosition] = useState(null);

  const dispatch = useDispatch();

  const onMarkAsRead = () => {
    if (!isRead && markable) {
      dispatch(markNotificationAsReadEffect({ ids }));
    }

    if (!isRead && !markable) {
      onMarkAllAsRead();
    }

    setActionPosition(null);
  };

  const onClear = () => {
    clearNotification({ Ids: ids }, (message) => {
      showSuccessMessage(message);
      dispatch(requestMarkNotificationAsClearedAction({ Ids: ids }));
    });
  };

  const onPin = (toggle) => {
    toggleNotification({ Id: ids?.[0] }, (message) => {
      showSuccessMessage(message);
      dispatch(requestMarkNotificationToggleAction({ Id: ids?.[0], toggle, pinned }));
    });
  };

  const onOpen = () => {
    onClick();
    if (markable) {
      onMarkAsRead();
    }
  };

  const getDate = () => {
    const notificationDate = moment(date);

    if (notificationDate.isSame(moment(), 'day')) {
      return notificationDate.fromNow(); // e.g., "2 hours ago"
    }

    return notificationDate.format('h:mm A'); // e.g., "3:42 PM"
  };

  const getActions = () => {
    const list = [
      { title: markable ? 'Open' : 'View', onClick: onOpen, testid: 'open_notification' },
    ];

    if (!isRead && markable) {
      list.push({ title: 'Mark as read', onClick: onMarkAsRead, testid: 'mark_as_read' });
    }

    if (!isRead && !markable) {
      list.push({ title: 'Mark all as read', onClick: onMarkAsRead, testid: 'mark_all_as_read' });
    }

    return list;
  };

  const onCloseActionList = () => {
    setActionPosition(null);
  };

  const severityLevelTag = () => {
    if (!severityLevel || severityLevel === SeverityLevel.LOW) {
      return null;
    }

    const text = severityLevel === SeverityLevel.MEDIUM ? 'Alert' : 'Urgent';
    const style = severityLevel === SeverityLevel.MEDIUM ? styles.tagAlert : styles.tagUrgent;

    return <Tag className={classNames(styles.tag, style)}>{text}</Tag>;
  };

  return (
    <div
      onClick={onOpen}
      className={classNames(className, styles.baseNotification)}
      testid="base_notification"
    >
      {markable && (
        <Popover
          overlayClassName={styles.popoverOverlay}
          placement="bottomRight"
          trigger="hover"
          content={
            <div className={styles.options}>
              {[
                {
                  icon: IsPinned ? Icon.UNPIN : Icon.PIN,
                  label: IsPinned ? 'Un-Pin' : 'Pin',
                  onClick: () => {
                    onPin(!IsPinned);
                  },
                },
                { icon: Icon.CLEAR, label: 'Clear', onClick: onClear },
              ].map(({ icon, label, onClick }) => (
                <ul className={styles.dropdown}>
                  <li
                    className={classNames(styles.item, { [styles.clear]: label === 'Clear' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenu(false);
                      onClick();
                    }}
                  >
                    <div className={styles.icon}>
                      <Icon variant={icon} />
                    </div>
                    <span>{label}</span>
                  </li>
                </ul>
              ))}
            </div>
          }
          open={openMenu}
          onOpenChange={(visible) => setOpenMenu(visible)}
          autoAdjustOverflow
        >
          <span
            className={styles.menu}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Icon variant={Icon.MENU} />
          </span>
        </Popover>
      )}
      {!isRead && <div className={styles.unreadDot} />}
      <Avatar
        className={styles.avatarWrapper}
        avatarClassName={styles.avatar}
        src={avatarUrl}
        placeholder={avatarPlaceholder}
      />
      <div className={styles.block}>
        <div className={styles.holder}>
          {!notificationSummary && (
            <div className={styles.inner}>
              <Title
                className={classNames(styles.title, {
                  [styles.isRead]: isRead,
                  [styles.markable]: markable,
                })}
              >
                {title}
              </Title>
            </div>
          )}
          {!notificationSummary && (
            <Subtitle className={classNames({ [styles.unread]: !isRead })} noMargin>
              <div>{subtitle}</div>
            </Subtitle>
          )}
          {notificationSummary &&
            (Array.isArray(notificationSummary) ? notificationSummary : [notificationSummary]).map(
              (item, index) => (
                <div key={index} className={styles.notificationSummaryGroup}>
                  <Title
                    className={classNames(styles.notificationSummaryTitle, {
                      [styles.isRead]: isRead,
                      [styles.markable]: markable,
                    })}
                  >
                    {typeof item === 'string' ? item : <BaseSummaryNotification summary={item} />}
                  </Title>
                </div>
              ),
            )}
          <Subtitle className={classNames({ [styles.unread]: !isRead })}>
            {markable && getDate()}
          </Subtitle>
        </div>
      </div>
      {!markable && (
        <div className={classNames(styles.date, { [styles.unread]: !isRead })} testid="time">
          <ArrowRight className={styles.arrow} />
        </div>
      )}
      {/**
       * Remvoving tags from the FE for now, the BE functionality is still intact and can be used later in the future.
       */}
      {/* <div>{severityLevelTag()}</div> */}
      {/* <ActionsList actions={getActions()} position={actionPosition} onClose={onCloseActionList} /> */}
    </div>
  );
};

BaseNotification.propTypes = {
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  className: PropTypes.string,
  avatarUrl: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  date: PropTypes.string,
  isRead: PropTypes.bool.isRequired,
  avatarPlaceholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.any]),
  onClick: PropTypes.func,
  groupType: PropTypes.string,
  markable: PropTypes.bool,
  onMarkAllAsRead: PropTypes.func,
  severityLevel: PropTypes.oneOf([SeverityLevel.LOW, SeverityLevel.MEDIUM, SeverityLevel.HIGH]),
};

BaseNotification.defaultProps = {
  className: '',
  avatarUrl: null,
  subtitle: '',
  avatarPlaceholder: undefined,
  onClick: () => {},
  markable: true,
  groupType: null,
  date: null,
  onMarkAllAsRead: () => {},
};

export default BaseNotification;
